import {
  MousePointer, Hand, PenTool, Move, Trash2,
  ZoomIn, ZoomOut, Save, FileDown, Layers,
  ClipboardCheck, Undo2, Ruler, Crosshair,
  Pentagon, Minus, Hash, BarChart3, Loader2, Sparkles, Wand2
} from 'lucide-react';

const TOOLS = [
  { key: 'select', icon: MousePointer, label: 'Select', shortcut: 'V' },
  { key: 'pan', icon: Hand, label: 'Pan', shortcut: 'H' },
  { key: 'draw', icon: PenTool, label: 'Draw', shortcut: 'D' },
  { key: 'edit', icon: Move, label: 'Edit', shortcut: 'E' },
  { key: 'delete', icon: Trash2, label: 'Delete', shortcut: 'X' },
];

const MEASURE_TOOLS = [
  { key: 'calibrate', icon: Crosshair, label: 'Calibrate', shortcut: 'C' },
  { key: 'measure', icon: Ruler, label: 'Distance', shortcut: 'M' },
];

const TAKEOFF_TOOLS = [
  { key: 'area', icon: Pentagon, label: 'Area', shortcut: 'A' },
  { key: 'linear', icon: Minus, label: 'Linear', shortcut: 'L' },
  { key: 'count', icon: Hash, label: 'Count', shortcut: 'N' },
];

const AI_TAKEOFF_TRADES = [
  'Mechanical', 'Electrical', 'Plumbing', 'Fire Protection',
  'Architectural', 'Structural', 'Roofing', 'Flooring', 'Painting',
  'Glazing', 'Civil', 'Landscape',
];

const STROKE_COLORS = [
  { value: '#10B981', label: 'Green' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#EF4444', label: 'Red' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#8B5CF6', label: 'Purple' },
];

export default function FloorPlanToolbar({
  mode, setMode, zoom, setZoom, setPan,
  saving, saveStatus, handleSave,
  exporting, handleExportPDF,
  handleChecklist, handlePickSheet,
  strokeColor, setStrokeColor,
  strokeWidth, setStrokeWidth,
  onUndoStroke, strokeCount,
  scaleCalibration, measureResult,
  takeoffMode, setTakeoffMode, setTakeoffPoints,
  takeoffPoints, onFinishTakeoff, savingMeasurement,
  showMeasurements, setShowMeasurements,
  measureLabel, setMeasureLabel,
  measureMaterial, setMeasureMaterial,
  measureTrade, setMeasureTrade,
  onDetectRooms, detectingRooms,
  onExportReport,
  onAiTakeoff, aiTakeoffRunning,
  onShowComparison,
  onBatchTakeoff,
}) {
  const isTakeoff = mode === 'takeoff';

  return (
    <div className="bg-card border-b border-border shrink-0" data-testid="fp-toolbar">
      {/* Main tool row */}
      <div className="flex flex-wrap items-center justify-between gap-y-1.5 gap-x-2 px-3 py-1.5">
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mr-1.5">Tools</span>
          {TOOLS.map(t => (
            <button key={t.key} onClick={() => { setMode(t.key); setTakeoffPoints?.([]); }} title={`${t.label} (${t.shortcut})`}
              data-testid={`fp-tool-${t.key}`}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                mode === t.key ? 'bg-primary/15 text-primary border border-primary/40' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
              }`}>
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
          <div className="w-px h-5 bg-border mx-1" />
          <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mr-1">Scale</span>
          {MEASURE_TOOLS.map(t => (
            <button key={t.key} onClick={() => { setMode(t.key); setTakeoffPoints?.([]); }} title={`${t.label} (${t.shortcut})`}
              data-testid={`fp-tool-${t.key}`}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                mode === t.key ? 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
              }`}>
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
          <div className="w-px h-5 bg-border mx-1" />
          <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mr-1">Takeoff</span>
          {TAKEOFF_TOOLS.map(t => (
            <button key={t.key} onClick={() => { setMode('takeoff'); setTakeoffMode(t.key); setTakeoffPoints?.([]); }} title={`${t.label} (${t.shortcut})`}
              data-testid={`fp-tool-takeoff-${t.key}`}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
                isTakeoff && takeoffMode === t.key
                  ? 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
              }`}>
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
          <div className="w-px h-5 bg-border mx-1" />
          <button onClick={() => setZoom(z => Math.max(z / 1.2, 0.2))} className="p-1 text-muted-foreground hover:text-foreground" data-testid="fp-zoom-out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-[9px] font-mono text-muted-foreground w-10 text-center" data-testid="fp-zoom-level">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(z * 1.2, 5))} className="p-1 text-muted-foreground hover:text-foreground" data-testid="fp-zoom-in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="p-1 text-[9px] text-muted-foreground hover:text-foreground font-mono" data-testid="fp-zoom-reset">
            Reset
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative group" data-testid="ai-takeoff-group">
            <button disabled={aiTakeoffRunning} data-testid="fp-ai-takeoff-btn"
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all disabled:opacity-50 peer">
              {aiTakeoffRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} AI Takeoff
            </button>
            <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-sm shadow-xl z-50 hidden group-hover:block min-w-[160px]" data-testid="ai-takeoff-dropdown">
              <div className="p-1.5">
                <p className="px-2 py-1 text-[9px] font-heading uppercase tracking-widest text-muted-foreground border-b border-border mb-1">Select Trade</p>
                {AI_TAKEOFF_TRADES.map(trade => (
                  <button key={trade} onClick={() => onAiTakeoff(trade)} disabled={aiTakeoffRunning}
                    data-testid={`ai-takeoff-trade-${trade.toLowerCase().replace(/\s+/g, '-')}`}
                    className="w-full text-left px-2 py-1.5 text-[11px] text-foreground hover:bg-primary/10 hover:text-primary rounded-sm transition-colors disabled:opacity-50">
                    {trade}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button onClick={onDetectRooms} disabled={detectingRooms} data-testid="fp-detect-rooms-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-violet-600 dark:text-violet-400 border border-violet-300 dark:border-violet-800 hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-all disabled:opacity-50">
            {detectingRooms ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Rooms
          </button>
          {onBatchTakeoff && (
            <button onClick={onBatchTakeoff} data-testid="fp-batch-takeoff-btn"
              className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-cyan-600 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-800 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 transition-all">
              <Layers className="w-3 h-3" /> Batch Takeoff
            </button>
          )}
          <button onClick={() => setShowMeasurements(!showMeasurements)} data-testid="fp-measurements-btn"
            className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all border ${
              showMeasurements ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800' : 'text-muted-foreground border-border hover:text-foreground hover:bg-muted/50'
            }`}>
            <BarChart3 className="w-3 h-3" /> Takeoffs
          </button>
          <button onClick={onShowComparison} data-testid="fp-compare-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border hover:text-foreground hover:bg-muted/50 transition-all">
            <BarChart3 className="w-3 h-3" /> Compare
          </button>
          <button onClick={handleChecklist} data-testid="fp-checklist-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border hover:text-foreground hover:bg-muted/50 transition-all">
            <ClipboardCheck className="w-3 h-3" /> Checklist
          </button>
          <button onClick={handlePickSheet} data-testid="fp-change-sheet-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider text-muted-foreground border border-border hover:text-foreground hover:bg-muted/50 transition-all">
            <Layers className="w-3 h-3" /> Sheets
          </button>
          <button onClick={handleSave} disabled={saving || saveStatus === 'saved'} data-testid="fp-save-btn"
            className={`flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-all ${
              saveStatus === 'saved' ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
              : saveStatus === 'unsaved' ? 'bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40'
              : 'text-muted-foreground border border-border hover:text-foreground'
            }`}>
            <Save className="w-3 h-3" /> {saving ? '...' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'unsaved' ? 'Save*' : 'Save'}
          </button>
          <button onClick={handleExportPDF} disabled={exporting} data-testid="fp-export-btn"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-heading uppercase tracking-wider bg-primary/10 text-primary border border-primary/30 hover:bg-primary/15 transition-all disabled:opacity-50">
            <FileDown className="w-3 h-3" /> {exporting ? '...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Context row for active tool */}
      {(mode === 'draw' || mode === 'calibrate' || mode === 'measure' || isTakeoff) && (
        <div className="h-8 flex items-center px-3 border-t border-border/50 bg-muted/10 gap-2">
          {mode === 'draw' && (
            <>
              <span className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground mr-1">Color</span>
              {STROKE_COLORS.map(c => (
                <button key={c.value} onClick={() => setStrokeColor(c.value)} title={c.label}
                  data-testid={`fp-stroke-color-${c.label.toLowerCase()}`}
                  className={`w-4 h-4 rounded-full border-2 transition-all ${
                    strokeColor === c.value ? 'border-foreground scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: c.value }} />
              ))}
              <div className="w-px h-4 bg-border mx-1" />
              {[2, 4, 6].map(w => (
                <button key={w} onClick={() => setStrokeWidth(w)}
                  className={`px-1.5 py-0.5 text-[9px] font-mono rounded-sm ${
                    strokeWidth === w ? 'bg-primary/15 text-primary' : 'text-muted-foreground'
                  }`}>
                  {w}px
                </button>
              ))}
              {strokeCount > 0 && (
                <button onClick={onUndoStroke} data-testid="fp-undo-stroke"
                  className="flex items-center gap-1 px-1.5 py-0.5 text-[9px] text-muted-foreground hover:text-foreground">
                  <Undo2 className="w-3 h-3" /> Undo
                </button>
              )}
            </>
          )}
          {mode === 'calibrate' && (
            <span className="text-[10px] text-amber-600 dark:text-amber-400 font-mono">
              Click two points on a known dimension, then enter the real distance
            </span>
          )}
          {mode === 'measure' && (
            <>
              {scaleCalibration?.calibrated ? (
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono flex items-center gap-1.5">
                  <Crosshair className="w-3 h-3" />
                  Scale: 1px = {(1/scaleCalibration.pixels_per_unit).toFixed(4)} {scaleCalibration.unit}
                  {measureResult && (
                    <span className="text-foreground font-medium ml-2">= {measureResult.display}</span>
                  )}
                </span>
              ) : (
                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-mono">Calibrate scale first</span>
              )}
            </>
          )}
          {isTakeoff && (
            <>
              <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                {takeoffMode === 'area' ? 'Click polygon vertices, double-click to finish area' :
                 takeoffMode === 'linear' ? 'Click points along the line, double-click to finish' :
                 'Click to place count markers, then click Save'}
              </span>
              {takeoffPoints.length > 0 && (
                <span className="text-[10px] font-mono text-muted-foreground ml-2">{takeoffPoints.length} points</span>
              )}
              <div className="w-px h-4 bg-border mx-1" />
              <input type="text" value={measureLabel} onChange={e => setMeasureLabel(e.target.value)}
                placeholder="Label..." data-testid="takeoff-label"
                className="px-2 py-0.5 text-[10px] bg-secondary border border-border rounded-sm text-foreground w-28" />
              <input type="text" value={measureMaterial} onChange={e => setMeasureMaterial(e.target.value)}
                placeholder="Material..." data-testid="takeoff-material"
                className="px-2 py-0.5 text-[10px] bg-secondary border border-border rounded-sm text-foreground w-28" />
              <input type="text" value={measureTrade} onChange={e => setMeasureTrade(e.target.value)}
                placeholder="Trade..." data-testid="takeoff-trade"
                className="px-2 py-0.5 text-[10px] bg-secondary border border-border rounded-sm text-foreground w-24" />
              {takeoffPoints.length >= (takeoffMode === 'area' ? 3 : takeoffMode === 'linear' ? 2 : 1) && (
                <button onClick={onFinishTakeoff} disabled={savingMeasurement} data-testid="takeoff-save-btn"
                  className="flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-heading uppercase bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50">
                  {savingMeasurement ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
              )}
              <button onClick={() => setTakeoffPoints([])} className="text-[10px] text-muted-foreground hover:text-foreground px-1">Clear</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
